import React, { useState, useEffect } from 'react';
import PointsInventoryAPI from '../points/PointsInventoryAPI.js';
import ReceiptAPI from '../points/ReceiptAPI.js';
import imageService from '../images/imageService.js';
import './PatientInventory.css';

const PatientInventory = ({ patientId }) => {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [userRole, setUserRole] = useState('');

  const inventoryApi = new PointsInventoryAPI();
  const receiptApi = new ReceiptAPI();
  const imageDir = 'inventory';

  useEffect(() => {
    fetchInventory();
    setUserRole(localStorage.getItem('role') || '');
  }, [patientId]);

  const fetchInventory = async () => {
    try {
      const data = await inventoryApi.getAll();
      setItems(data);
    } catch (error) {
      console.error('Inventory fetch error:', error);
    }
  };

  const handleBuyClick = (item) => {
    setSelectedItem(item);
    setPopupVisible(true);
  };

  const confirmPurchase = async () => {
    if (!selectedItem) return;
    try {
      const newReceipt = {
        inventoryId: selectedItem.inventory_id,
        pointsBankId: patientId,
        amountTotal: selectedItem.price,
        amountBefore: 0,
        amountAfter: 0,
        date: new Date().toISOString().split('T')[0],
      };
      await receiptApi.add(newReceipt);
      await fetchInventory();
      showPopup('Purchase successful!');
    } catch (error) {
      console.error('Purchase error:', error);
      showPopup('Error completing purchase.');
    } finally {
      setSelectedItem(null);
      setPopupVisible(false);
    }
  };

  const cancelPurchase = () => {
    setSelectedItem(null);
    setPopupVisible(false);
  };

  const showPopup = (message) => {
    setPopupMessage(message);
    setTimeout(() => setPopupMessage(''), 3000);
  };

  // Helper: only show if picture is a non-empty, non-"null/undefined" string
  const hasImage = (picture) => {
    if (picture === 0) return true; // in case IDs are used
    const s = String(picture ?? '').trim().toLowerCase();
    return s.length > 0 && s !== 'null' && s !== 'undefined';
  };

  return (
    <div className="patient-inventory">
      {popupMessage && <div className="popup-message">{popupMessage}</div>}

      {/* 🏆 Points & Rewards Info Section */}
      <section className="points-explanation">
        <h1>🏆 Points & Rewards Program</h1>
        <p><strong>What Are Points?</strong></p>
        <p>
          Our Points & Rewards program is designed to motivate and appreciate our patients for their continued engagement and feedback.
          Points act as a virtual currency earned through various interactions and can be redeemed for select services—offering added value at no extra cost.
        </p>
        <p><strong>How to Earn Points</strong></p>
        <p><strong>Appointment Booking:</strong> Earn 1 point each time you book an appointment through our website.</p>
        <p><strong>Leave a Private Rating:</strong> After your appointment, provide a private rating to management and earn 1 point.</p>
        <p><strong>Special Promotions:</strong> Participate in bonus events, referrals, or seasonal campaigns for extra points.</p>
        <p><strong>How to Spend Points</strong></p>
        <p>Redeem points for exclusive services in our rewards catalog.</p>
        <p>Services are point-based—no cash required.</p>
        <p>Points have no cash value and are non-transferable.</p>
        <p><strong>🎁 Promotions</strong></p>
        <p>We frequently run limited-time promotions to help you:</p>
        <p>Earn more points through bonus campaigns.</p>
        <p>Spend points on discounted services or special packages.</p>
        <p>Watch for announcements and make the most of your points!</p>
        <hr />
      </section>

      {/* Inventory Section */}
      <h2>Available Rewards</h2>
      <div className="inventory-list">
        {items.map((item) => {
          const showImg = hasImage(item.picture);
          const src = showImg ? imageService.getImageUrl(imageDir, item.picture) : null;

          return (
            <div key={item.inventory_id} className="inventory-card">
              {showImg && (
                <img
                  src={src}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  // If the URL 404s, just hide the image (no default.png fetch)
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <p><strong>Points:</strong> {item.price}</p>
              {userRole === 'PATIENT' ? (
                <button onClick={() => handleBuyClick(item)}>Buy</button>
              ) : (
                <p className="view-only-note">Login as a patient to redeem this reward.</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Confirmation Popup */}
      {popupVisible && selectedItem && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h2>Confirm Purchase</h2>
            <p>Do you want to redeem <strong>{selectedItem.price}</strong> points for <strong>{selectedItem.title}</strong>?</p>
            <div className="popup-actions">
              <button onClick={confirmPurchase}>Confirm</button>
              <button onClick={cancelPurchase}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientInventory;
